import React, { useRef, useState, useEffect } from "react";
import axios from "axios";
import QRCode from "react-qr-code";
import { useReactToPrint } from "react-to-print";
import { apiKey } from "../../config/config";
import { languageHelper } from "../../config/helperFunctions";
import {
  FaTimes,
  FaPrint,
} from "react-icons/fa";

export default function CustomerCardModal({
  customer,
  onClose,
  onRegisterNext,
  countdown,
  outletData: initialOutletData,
  logoUrl: initialLogoUrl,
}) {
  const cardRef = useRef(null);
  const [outletData, setOutletData] = useState(initialOutletData || null);
  const [logoUrl, setLogoUrl] = useState(initialLogoUrl || null);

  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem("rms-admin"));
    } catch {
      return null;
    }
  })();

  // Self-contained fetch if logoUrl or outletData are not provided as props
  useEffect(() => {
    if (!initialOutletData && user?.user?.outlet_id && user?.token) {
      axios
        .get(`${apiKey}/api/rms/admin/get-outlets/${user.user.outlet_id}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        })
        .then((res) => {
          setOutletData(res.data && res.data[0]);
        })
        .catch(() => {});
    }

    if (!initialLogoUrl) {
      axios
        .get(`${apiKey}/api/rms/customer/settings`)
        .then((res) => {
          const logoItem = res.data?.find((item) => item.type === "logo");
          if (logoItem?.value) {
            try {
              const parsed = JSON.parse(logoItem.value);
              if (parsed?.logo_image_url) {
                setLogoUrl(parsed.logo_image_url);
              }
            } catch {}
          }
        })
        .catch(() => {});
    }
  }, [initialOutletData, initialLogoUrl, user?.token, user?.user?.outlet_id]);

  // Hook must be called unconditionally before any early returns
  const handlePrintCard = useReactToPrint({
    content: () => cardRef.current,
    documentTitle: `Customer-Card-${customer?.name || "Member"}`,
  });

  if (!customer) return null;

  // JSON payload stored in QR code as requested: { customer_id, customer_name, customer_phone }
  const qrJsonValue = JSON.stringify({
    customer_id: customer.id,
    customer_name: customer.name,
    customer_phone: customer.phone,
  });

  const formattedDate = customer.created_at
    ? new Date(customer.created_at).toLocaleDateString("en-GB")
    : new Date().toLocaleDateString("en-GB");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-5 sm:p-6 text-center relative border border-gray-200 my-auto animate-fadeIn"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          type="button"
          className="absolute top-4 end-4 text-gray-400 hover:text-gray-600 p-1.5 rounded-full hover:bg-gray-100 transition z-20"
          title={languageHelper("Close")}
        >
          <FaTimes className="text-lg" />
        </button>

        {/* Notice to user: Take picture of the card */}
        <div className="my-6 bg-amber-50 border-2 border-amber-300 text-amber-900 rounded-2xl p-3.5 shadow-sm text-center">
          <div className="flex items-center justify-center gap-2 text-sm sm:text-base font-extrabold text-amber-900">
            <span>{languageHelper("Please take a picture of this card!")}</span>
          </div>
          <p className="text-xs text-amber-800 mt-1 font-medium">
            {languageHelper(
              "Keep this photo on your phone to scan and redeem discounts on every visit."
            )}
          </p>
          <p className="text-[11px] text-amber-700 font-semibold mt-0.5" dir="rtl">
            📸 يرجى التقاط صورة للبطاقة لإبراز الرمز عند زيارتك القادمة والحصول على الخصم
          </p>
        </div>

        {/* Visual Digital Membership Card */}
        <div className="flex justify-center my-12">
          <div
            ref={cardRef}
            className="w-[320px] sm:w-[340px] h-[480px] sm:h-[500px] rounded-3xl p-6 relative overflow-hidden shadow-2xl flex flex-col justify-between text-white select-none border border-white/20 bg-d-bg"
          >
            {/* Glow Accents */}
            <div className="absolute -top-12 -right-12 w-36 h-36 bg-amber-400/20 rounded-full blur-2xl pointer-events-none" />
            <div className="absolute -bottom-12 -left-12 w-36 h-36 bg-purple-300/20 rounded-full blur-2xl pointer-events-none" />

            {/* Card Top: Logo & Membership Badge */}
            <div className="flex justify-between items-start relative z-10">
              <div className="flex items-center gap-2">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt="Restaurant Logo"
                    className="h-10 max-w-[140px] object-contain brightness-0 invert drop-shadow"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/assets/logo/imdady_logo.png";
                    }}
                  />
                ) : (
                  <div className="flex items-center gap-1.5">
                    <img
                      src="/assets/logo/imdady_logo.png"
                      alt="Logo"
                      className="h-8 brightness-0 invert drop-shadow"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                    <span className="font-extrabold text-white text-base tracking-tight">
                      {outletData?.name || "Imdady"}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Card Middle: Customer Details */}
            <div className="relative z-10 px-1 pt-3 pb-2 border-t border-white/20 flex justify-between items-end">
              <div className="text-start">
                <span className="block text-[10px] uppercase font-bold tracking-wider text-purple-200">
                  {languageHelper("Member Name")} / نوع البطاقة
                </span>
                <h4 className="text-xl font-black text-white tracking-wide max-w-[190px] drop-shadow-sm">
                  {customer.name}
                </h4>
                <span className="text-xs text-purple-200 block font-mono" dir="ltr">
                  {customer.phone}
                </span>
              </div>

              <div className="text-end">
                <span className="block text-[10px] uppercase font-bold tracking-wider text-purple-200">
                  {languageHelper("Joined")} / التاريخ
                </span>
                <span className="text-xs font-bold text-white block">
                  {formattedDate}
                </span>
                <span className="inline-block text-[10px] font-semibold text-emerald-300 mt-0.5">
                  ● {languageHelper("Active")}
                </span>
              </div>
            </div>

            {/* Card Bottom: White QR Box */}
            <div className="relative z-10 flex flex-col items-center justify-center pt-2">
              <div className="bg-white p-3.5 rounded-2xl shadow-xl flex flex-col items-center border border-purple-200">
                <QRCode
                  value={qrJsonValue}
                  size={140}
                  level="M"
                />
                <span className="text-gray-900 font-mono font-black text-sm tracking-widest mt-2">
                  #{String(customer.id || "").padStart(5, "0")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Action Buttons */}
        <div className="mt-4 space-y-2">

            <button
              type="button"
              onClick={handlePrintCard}
              className="py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 transition shadow-sm mx-auto w-40"
              title="Print Card"
            >
              <FaPrint />
              <span>{languageHelper("Print")}</span>
            </button>

          <div className="flex gap-2 pt-1">
            {onRegisterNext ? (
              <button
                type="button"
                onClick={onRegisterNext}
                className="flex-1 py-2.5 bg-d-cat-btn text-white rounded-xl font-bold text-sm shadow hover:opacity-90 transition"
              >
                {languageHelper("Register Next Customer")}
                {typeof countdown === "number" ? ` (${countdown}s)` : ""}
              </button>
            ) : null}

            <button
              type="button"
              onClick={onClose}
              className={`py-2.5 px-5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-semibold transition ${
                !onRegisterNext ? "flex-1" : ""
              }`}
            >
              {languageHelper("Close")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
